import { FC } from 'lib/component-utils'
import { GetServerSideProps } from 'next'
import { withUserProps } from 'lib/contextCreation'
import { useImmer } from 'use-immer'
import AuthLayout from 'components/auth/AuthLayout'
import { LoginProcessState } from 'components/auth/lib/types'
import ConfirmationCodeForm from 'components/auth/ConfirmationCodeForm'
import { useIntl } from 'lib/intl-utils'
import LoginForm from 'components/auth/LoginForm'
import PasskeyIntegration from './PasskeyIntegration'
import { addInternalRedirectSearchParam, getSafeInternalRedirect } from 'lib/redirect'
import { useUserContext } from 'lib/context'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import MessengerAuth from './MessengerAuth'
import LegalText from './LegalText'
import { getLegalDocuments } from 'configs/legal'
import Button from 'components/base/Button'
import Link from 'components/base/Link'
import type { MessengerAuthProvider } from 'lib/dto'
import {
  platformAuthenticatorIsAvailable,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser'
import { FingerPrintIcon } from '@heroicons/react/outline'
import { useToast } from 'components/app/NotificationArea'
import { useSetAtom } from 'jotai'
import { loginSignalAtom } from './lib/atoms'

export const getServerSideProps: GetServerSideProps = withUserProps(
  async ({ pageContext, userContext }) => {
    const redirect = getSafeInternalRedirect(pageContext.query.redirect) ?? null
    if (userContext.isAuthorized) {
      return {
        redirect: {
          permanent: false,
          destination: redirect ?? '/products/list/',
          ...(redirect ? { basePath: false as const } : {}),
        },
      }
    }

    const error = pageContext.query.error ?? null

    return {
      props: {
        isResponsive: true,
        disableFooter: true,
        redirect,
        error,
        bots: {
          telegram: userContext.theme.notificationBot ?? '',
          max: userContext.theme.maxNotificationBot ?? '',
        },
      },
    }
  },
  { allowUnauthorizedUsers: true, forceUnauthorizedMode: process.env.NODE_ENV === 'development' }
)

type Props = {
  redirect?: string
  error?: string
  bots: Record<MessengerAuthProvider, string>
}

const LoginPage: FC<Props> = ({ redirect, error, bots }) => {
  const { t } = useIntl()
  const ctx = useUserContext()
  const router = useRouter()
  const [smsSubmitting, setSmsSubmitting] = useState(false)
  const [platformAuthenticatorAvailable, setPlatformAuthenticatorAvailable] = useState(false)
  const [passkeySubmitting, setPasskeySubmitting] = useState(false)
  const showToast = useToast()
  const setLoginSignal = useSetAtom(loginSignalAtom)
  useEffect(() => {
    if (ctx.theme.hostType !== 'mayak') return
    let active = true
    platformAuthenticatorIsAvailable()
      .then((available) => {
        if (active) setPlatformAuthenticatorAvailable(available)
      })
      .catch(() => {})
    return () => {
      active = false
      WebAuthnAbortService.cancelCeremony()
    }
  }, [ctx.theme.hostType])
  const legalDocuments = getLegalDocuments(ctx.theme.hostType)
  const plan = typeof router.query.plan === 'string' ? router.query.plan : undefined
  const signupParams = new URLSearchParams()
  for (const key of [
    'plan',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'actionpay',
    'promoCode',
  ]) {
    if (typeof router.query[key] === 'string') signupParams.set(key, router.query[key] as string)
  }
  const signupHref = `/signup/${signupParams.size ? `?${signupParams}` : ''}`

  const [processState, setProcessState] = useImmer<LoginProcessState>({
    type: 'login',
    stage: 'enteringData',
    data: null,
    codeType: 'sms',
    phone: '',
  })

  const form = (
    <>
      {(processState.stage === 'enteringData' || processState.stage === 'waitingForPasskey') && (
        <LoginForm
          processState={processState}
          setProcessState={setProcessState}
          redirect={redirect}
          error={error}
          hideLegalText={ctx.theme.hostType === 'mayak'}
          hideSignupLink={ctx.theme.hostType === 'mayak'}
          hidePasskey={ctx.theme.hostType === 'mayak'}
          onSubmittingChange={setSmsSubmitting}
        />
      )}
      {processState.stage === 'waitingForCode' && (
        <ConfirmationCodeForm
          processState={processState}
          setProcessState={setProcessState}
          redirect={redirect}
          onSubmittingChange={setSmsSubmitting}
        />
      )}
      <PasskeyIntegration
        processState={processState}
        setProcessState={setProcessState}
        redirect={redirect}
      />
    </>
  )

  if (ctx.theme.hostType === 'mayak' && processState.stage === 'success') {
    return (
      <AuthLayout pageName={t`login.login`} hideOgImage>
        {form}
      </AuthLayout>
    )
  }

  return (
    <AuthLayout pageName={t`login.login`} hideOgImage>
      {ctx.theme.hostType === 'mayak' ? (
        <>
          <MessengerAuth
            legalBlock={
              <p className="mt-8 text-xs text-center text-gray-500">
                <LegalText
                  message={t`auth_legal.sign_in_offer_privacy`}
                  links={{
                    offer: { href: legalDocuments.offer, label: t`auth_legal.offer` },
                    privacy: { href: legalDocuments.privacyPolicy, label: t`auth_legal.privacy` },
                  }}
                />
              </p>
            }
            mode="login"
            bots={bots}
            redirect={redirect}
            plan={plan}
            smsStage={processState.stage}
            smsBusy={
              smsSubmitting ||
              passkeySubmitting ||
              processState.stage === 'waitingForPasskey' ||
              processState.stage === 'success'
            }
            passkeyButton={
              platformAuthenticatorAvailable && (
                <div className="flex flex-col">
                  <Button
                    theme={ctx.lastLoginType === 'webauthn' ? 'primary' : 'white'}
                    disabled={passkeySubmitting || processState.stage === 'success'}
                    className="w-full justify-center"
                    onClick={async () => {
                      if (passkeySubmitting || processState.stage === 'success') return
                      setPasskeySubmitting(true)
                      try {
                        const options = await ctx.getLogInWithAuthenticatorOptions()
                        const credential = await startAuthentication({ optionsJSON: options })
                        const response = await ctx.logInWithAuthenticator(credential)
                        if (response.status !== 'ok') throw new Error('Passkey login failed')
                        setProcessState((draft) => {
                          draft.stage = 'success'
                        })
                        setTimeout(() => setLoginSignal((value) => !value), 5000)
                        const safeRedirect = getSafeInternalRedirect(redirect)
                        if (safeRedirect) window.location.assign(safeRedirect)
                        else void router.push('/products/list/')
                      } catch (error) {
                        if (
                          !(error instanceof Error) ||
                          (error.name !== 'AbortError' && error.name !== 'NotAllowedError')
                        ) {
                          showToast('error', t`error`, t`login.webauthn_error`)
                        }
                      } finally {
                        setPasskeySubmitting(false)
                      }
                    }}
                  >
                    <FingerPrintIcon className="size-5" />
                    <span>
                      {passkeySubmitting ? t`login.waiting_for_passkey` : t`login.try_webauthn`}
                    </span>
                  </Button>
                  {ctx.lastLoginType === 'webauthn' && (
                    <div className="mt-1 text-center text-[10px] text-gray-500">
                      {t`login.you_used_this_last_time`}
                    </div>
                  )}
                </div>
              )
            }
            onChooseMethod={() =>
              setProcessState((draft) => {
                draft.stage = 'enteringData'
              })
            }
          >
            {form}
          </MessengerAuth>
          {processState.stage !== 'waitingForCode' && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 text-sm text-gray-500 whitespace-nowrap">
              <span>{t`login.not_registered`}?</span>
              <Button
                theme="white"
                as={Link}
                href={
                  redirect
                    ? addInternalRedirectSearchParam(signupHref, 'redirect', redirect)
                    : signupHref
                }
              >
                {t`login.sign_up`}
              </Button>
            </div>
          )}
        </>
      ) : (
        form
      )}
    </AuthLayout>
  )
}

export default LoginPage
